// ======================================================================
// FILE: src/modules/campaign/layout/CampaignSidebar.jsx
// MODULE: Campaign Management
// PURPOSE:
//   Sidebar navigation for Campaign module
//   - RBAC driven
//   - ERP standard layout
//   - Collapsible sidebar
// ======================================================================

import React, {
  useContext,
  useEffect,
  useState,
} from "react";

import {
  NavLink,
  useLocation,
  Link,
} from "react-router-dom";

import { AuthContext }
from "../../../context/AuthContext";

// ----------------------------------------------------------------------
// PERMISSION HELPER
// ----------------------------------------------------------------------

const canAny = (
  permissions,
  keys = []
) =>
  keys.some(
    (k) => permissions?.[k]?.view === true
  );

// ----------------------------------------------------------------------
// MENU
// ----------------------------------------------------------------------

const MENU = [

    // Dashboard
    {
        label: "Dashboard",
        route: "/campaign/dashboard",
        perms: ["CAMPAIGN_DASHBOARD"],
        icon: "fas fa-chart-line",
    },

    // Operations
    {
        type: "group",
        label: "Operations",
    },

    {
        label: "Prospect Import",
        route: "/campaign/prospect-import",
        perms: ["CAMPAIGN_PROSPECT_IMPORT"],
        icon: "fas fa-file-import",
    },

    {
        label: "Campaigns",
        route: "/campaign/campaigns",
        perms: ["CAMPAIGN_CAMPAIGNS"],
        icon: "fas fa-bullhorn",
    },

    {
        label: "Prospect Management",
        route: "/campaign/prospects",
        perms: ["CAMPAIGN_PROSPECT_MANAGEMENT"],
        icon: "fas fa-users",
    },

    {
        label: "Activities",
        route: "/campaign/activities",
        perms: ["CAMPAIGN_ACTIVITIES"],
        icon: "fas fa-calendar-check",
    },

    {
        label: "Assignment",
        route: "/campaign/assignment",
        perms: ["CAMPAIGN_ASSIGNMENT"],
        icon: "fas fa-user-check",
    },

    // Reports
    {
        type: "group",
        label: "Reports",
    },

    {
        label: "Campaign Report",
        route: "/campaign/reports/campaign",
        perms: ["CAMPAIGN_REPORT"],
        icon: "fas fa-chart-bar",
    },

    {
        label: "Prospect Report",
        route: "/campaign/reports/prospect",
        perms: ["CAMPAIGN_PROSPECT_REPORT"],
        icon: "fas fa-address-book",
    },

    {
        label: "Assignment Report",
        route: "/campaign/reports/assignment",
        perms: ["CAMPAIGN_ASSIGNMENT_REPORT"],
        icon: "fas fa-tasks",
    },

    {
        label: "Activity Report",
        route: "/campaign/reports/activity",
        perms: ["CAMPAIGN_ACTIVITY_REPORT"],
        icon: "fas fa-history",
    },

    // Masters
    {
        type: "group",
        label: "Masters",
    },

    {
        label: "Campaign Type",
        route: "/campaign/masters/campaign-type",
        perms: ["CAMPAIGN_TYPE"],
        icon: "fas fa-layer-group",
    },

    {
        label: "Lead Source",
        route: "/campaign/masters/lead-source",
        perms: ["CAMPAIGN_LEAD_SOURCE"],
        icon: "fas fa-sign-in-alt",
    },

    {
        label: "Industry",
        route: "/campaign/masters/industry",
        perms: ["CAMPAIGN_INDUSTRY"],
        icon: "fas fa-industry",
    },

    {
        label: "Prospect Status",
        route: "/campaign/masters/prospect-status",
        perms: ["CAMPAIGN_PROSPECT_STATUS"],
        icon: "fas fa-flag",
    },

    {
        label: "Priority",
        route: "/campaign/masters/priority",
        perms: ["CAMPAIGN_PRIORITY"],
        icon: "fas fa-exclamation-circle",
    },

    {
        label: "Tags",
        route: "/campaign/masters/tags",
        perms: ["CAMPAIGN_TAGS"],
        icon: "fas fa-tags",
    },

    // Configuration
    {
        type: "group",
        label: "Configuration",
    },

    {
        label: "Number Series",
        route: "/campaign/config/number-series",
        perms: ["CAMPAIGN_NUMBER_SERIES"],
        icon: "fas fa-sort-numeric-up",
    },

    {
        label: "Assignment Rules",
        route: "/campaign/config/assignment-rules",
        perms: ["CAMPAIGN_ASSIGNMENT_RULES"],
        icon: "fas fa-random",
    },

    {
        label: "Notification Rules",
        route: "/campaign/config/notification-rules",
        perms: ["CAMPAIGN_NOTIFICATION_RULES"],
        icon: "fas fa-bell",
    },

    {
        label: "Settings",
        route: "/campaign/settings",
        perms: ["CAMPAIGN_SETTINGS"],
        icon: "fas fa-cogs",
    }

];

// ======================================================================
// COMPONENT
// ======================================================================

export default function CampaignSidebar({
  onToggle,
}) {

  const { permissions } =
    useContext(AuthContext);

  const location =
    useLocation();

  const collapsedLS =
    localStorage.getItem(
      "campaign_sidebar_collapsed"
    );

  const [expanded, setExpanded] =
    useState(
      collapsedLS === null
        ? true
        : collapsedLS === "false"
    );

  useEffect(() => {

    localStorage.setItem(
      "campaign_sidebar_collapsed",
      expanded ? "false" : "true"
    );

    onToggle?.(expanded);

  }, [expanded, onToggle]);

  return (

    <aside
      style={{
        width: expanded ? 260 : 64,
        transition: "width 0.22s ease",
        background: "#111827",
        color: "#fff",
        minHeight: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
      }}
    >

      {/* HEADER */}

      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded
            ? "space-between"
            : "center",
          padding: expanded
            ? "0 12px"
            : "0",
          borderBottom:
            "1px solid #374151",
        }}
      >

        {expanded && (
          <span
            style={{
              fontWeight: 700,
            }}
          >
            Campaign
          </span>
        )}

        <button
          onClick={() =>
            setExpanded((v) => !v)
          }
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
          }}
        >
          <i className="fas fa-bars" />
        </button>

      </div>

      {/* BACK TO DASHBOARD */}

      <Link
        to="/dashboard"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: 12,
          padding: "10px 12px",
          borderRadius: 999,
          background: "#2563eb",
          color: "#fff",
          textDecoration: "none",
          justifyContent: expanded
            ? "flex-start"
            : "center",
        }}
      >
        <i className="fas fa-home" />

        {expanded && (
          <span>
            Back to Dashboard
          </span>
        )}

      </Link>

      {/* MENU */}

      <nav
        style={{
          maxHeight:
            "calc(100vh - 140px)",
          overflowY: "auto",
        }}
      >

        {MENU.map((item, idx) => {

          if (
            item.type === "group"
          ) {

            if (!expanded)
              return null;

            return (
              <div
                key={`grp-${idx}`}
                style={{
                  margin:
                    "12px 14px 6px",
                  fontSize: 11,
                  color: "#9ca3af",
                  textTransform:
                    "uppercase",
                }}
              >
                {item.label}
              </div>
            );
          }

          if (
            !canAny(
              permissions,
              item.perms
            )
          ) {
            return null;
          }

          const active =
            location.pathname.startsWith(
              item.route
            );

          return (

            <NavLink
              key={item.route}
              to={item.route}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: expanded
                  ? "10px 14px"
                  : "10px 0",
                justifyContent:
                  expanded
                    ? "flex-start"
                    : "center",
                color: active
                  ? "#fff"
                  : "#cbd5e1",
                background: active
                  ? "#374151"
                  : "transparent",
                textDecoration:
                  "none",
                fontSize: 14,
              }}
            >
              <i
                className={
                  item.icon
                }
              />

              {expanded && (
                <span>
                  {item.label}
                </span>
              )}

            </NavLink>

          );

        })}

      </nav>

    </aside>

  );

}